// tabs/TemplatesTab.jsx
import React, { useEffect, useState } from "react";
import {
  FaClone,
  FaEdit,
  FaEnvelopeOpenText,
  FaPlus,
  FaSync,
  FaTrash,
} from "react-icons/fa";

import {
  deleteEmailTemplate,
  duplicateEmailTemplate,
  getEmailTemplates,
} from "../../../../../services/mailingServices";

import { getProductLabel } from "../constants";

export default function TemplatesTab({
  refreshKey = 0,
  onCreateTemplate,
  onEditTemplate,
}) {
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const rows = await getEmailTemplates();
      setTemplates(rows || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
    // eslint-disable-next-line
  }, [refreshKey]);

  const onDuplicate = async (id) => {
    await duplicateEmailTemplate(id);
    fetchTemplates();
  };

  const onDelete = async (id) => {
    if (!window.confirm("Supprimer ou archiver ce modèle ?")) return;
    await deleteEmailTemplate(id);
    fetchTemplates();
  };

  return (
    <div className="rounded-3xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm p-5">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Modèles d’emails</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Créez vos emails professionnels avec titre, texte, image, bouton et
            signature.
          </p>
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={fetchTemplates}
            className="inline-flex items-center gap-2 rounded-2xl bg-slate-100 dark:bg-slate-800 px-3 py-2 text-sm"
          >
            <FaSync /> Actualiser
          </button>

          <button
            type="button"
            onClick={onCreateTemplate}
            className="inline-flex items-center gap-2 rounded-2xl bg-slate-950 text-white dark:bg-slate-100 dark:text-slate-950 px-3 py-2 text-sm"
          >
            <FaPlus /> Nouveau modèle
          </button>
        </div>
      </div>

      <div className="mt-5 grid gap-3">
        {loading ? (
          <div className="text-sm text-slate-500">Chargement…</div>
        ) : templates.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-slate-300 dark:border-slate-700 p-8 text-center">
            <FaEnvelopeOpenText className="mx-auto text-3xl text-slate-400" />
            <div className="mt-3 font-semibold">Aucun modèle</div>
            <p className="mt-1 text-sm text-slate-500">
              Ajoutez votre premier modèle d’email avant de créer une campagne.
            </p>

            <button
              type="button"
              onClick={onCreateTemplate}
              className="mt-4 inline-flex items-center gap-2 rounded-2xl bg-slate-950 text-white dark:bg-slate-100 dark:text-slate-950 px-4 py-2 text-sm"
            >
              <FaPlus /> Créer un modèle
            </button>
          </div>
        ) : (
          templates.map((template) => (
            <TemplateCard
              key={template.id}
              template={template}
              onEdit={() => onEditTemplate(template)}
              onDuplicate={() => onDuplicate(template.id)}
              onDelete={() => onDelete(template.id)}
            />
          ))
        )}
      </div>
    </div>
  );
}

function TemplateCard({ template, onEdit, onDuplicate, onDelete }) {
  return (
    <div className="rounded-3xl border border-slate-200 dark:border-slate-800 p-4 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-semibold">{template.name}</h3>

            <span
              className={[
                "rounded-full px-2.5 py-1 text-xs",
                template.status === "active"
                  ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-200"
                  : template.status === "draft"
                    ? "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200"
                    : "bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-200",
              ].join(" ")}
            >
              {template.status}
            </span>
          </div>

          <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            {getProductLabel(template.product_key)} • slug : {template.slug}
          </div>

          <div className="mt-3 text-sm">
            <span className="text-slate-500">Sujet : </span>
            <span className="font-medium">{template.subject}</span>
          </div>

          {template.preheader && (
            <div className="mt-1 text-xs text-slate-500">
              {template.preheader}
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={onEdit}
            className="inline-flex items-center gap-2 rounded-xl bg-slate-100 dark:bg-slate-800 px-3 py-2 text-sm"
          >
            <FaEdit /> Éditer
          </button>

          <button
            type="button"
            onClick={onDuplicate}
            className="inline-flex items-center gap-2 rounded-xl bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-200 px-3 py-2 text-sm"
          >
            <FaClone /> Dupliquer
          </button>

          <button
            type="button"
            onClick={onDelete}
            className="inline-flex items-center gap-2 rounded-xl bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-200 px-3 py-2 text-sm"
          >
            <FaTrash /> Suppr.
          </button>
        </div>
      </div>
    </div>
  );
}